/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PublishIcon from "@mui/icons-material/Publish";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { Pagination, WarnModal } from "../../components";
import { Loader, showError, showToast } from "../../constants";
import { GetNews, UpdateNewsStatus, DeleteNews } from "../../services/home";
import { News } from "../../types/General";
import { isValidInput } from "../../utils/validations";

const ITEMS_PER_PAGE = 10;

const NewsPage = () => {
  const navigate = useNavigate();

  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Action menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNews, setSelectedNews] = useState<News | null>(null);

  // Delete modal
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = (await GetNews({
        limit: ITEMS_PER_PAGE,
        skip: (page - 1) * ITEMS_PER_PAGE,
        search: debouncedSearch,
        status: statusFilter,
      })) as { status: number; data: News[]; count: number };
      if (response?.status === 200) {
        setNews(response.data || []);
        setTotalCount(response.count || 0);
      } else {
        setNews([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to fetch news");
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedSearch(searchTerm), 1000);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  useEffect(() => {
    fetchNews();
  }, [page, debouncedSearch, statusFilter]);

  const onPageChange = (newPage: number) => setPage(newPage);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: News) => {
    setAnchorEl(event.currentTarget);
    setSelectedNews(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNews(null);
  };

  const handleStatusToggle = async () => {
    if (!selectedNews) return;
    const newStatus = selectedNews.status === "Published" ? "Disabled" : "Published";
    try {
      setLoading(true);
      const response = (await UpdateNewsStatus(selectedNews._id, { status: newStatus })) as { status: number };
      if (response?.status === 200) {
        showToast(`News ${newStatus === "Published" ? "published" : "disabled"} successfully`);
        fetchNews();
      }
    } catch (error) {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    try {
      if (!deleteId) {
        showError("Please try again");
        return;
      }
      setLoading(true);
      const response = (await DeleteNews(deleteId)) as { status: number };
      if (response?.status === 200) {
        showToast("News deleted successfully");
        fetchNews();
      }
    } catch (error) {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const truncateText = (text: string | undefined | null, wordLimit = 8) => {
    if (!text) return "";
    const words = text.split(" ");
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + "...";
  };

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_loyout">
        <div className="dashboard">
          <div className="heading-bread">
            <h1 className="mn_hdng">News</h1>
          </div>
        </div>

        <Card className="cards">
          <Box className="cards_header">
            <Grid
              container
              spacing={2}
              sx={{ mb: 1 }}
              alignItems="center"
              justifyContent="space-between"
            >
              {/* Left: Search */}
              <Grid item xs={12} md="auto">
                <TextField
                  placeholder="Search by title"
                  value={searchTerm}
                  size="small"
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      onPageChange(1);
                      setSearchTerm(e.target.value);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: "100%", md: 280 } }}
                />
              </Grid>

              {/* Right: Filter + Add */}
              <Grid item xs={12} md="auto">
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                  }}
                >
                  {/* Status Filter */}
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      displayEmpty
                      sx={{ borderRadius: "8px", fontSize: "13px" }}
                    >
                      <MenuItem value="">All Status</MenuItem>
                      <MenuItem value="Draft">Draft</MenuItem>
                      <MenuItem value="Published">Published</MenuItem>
                      <MenuItem value="Disabled">Disabled</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    sx={{ height: 40 }}
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/manage-news/add")}
                  >
                    Add News
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table */}
          <TableContainer className="table_container">
            <Table sx={{ minWidth: 650 }} aria-label="news table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">TITLE</TableCell>
                  <TableCell align="left">DESCRIPTION</TableCell>
                  <TableCell align="center">STATUS</TableCell>
                  <TableCell align="center">DATE</TableCell>
                  <TableCell align="center">ACTIONS</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {news.length ? (
                  news.map((item) => (
                    <TableRow key={item._id}>
                      <TableCell align="left">
                        {truncateText(item.title, 6)}
                      </TableCell>
                      <TableCell align="left">
                        {truncateText(item.description, 10)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.status}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: "11px",
                            backgroundColor:
                              item.status === "Published"
                                ? "#dcfce7"
                                : item.status === "Draft"
                                  ? "#fef9c3"
                                  : item.status === "Disabled"
                                    ? "#fee2e2"
                                    : "#f1f5f9",
                            color:
                              item.status === "Published"
                                ? "#16a34a"
                                : item.status === "Draft"
                                  ? "#ca8a04"
                                  : item.status === "Disabled"
                                    ? "#dc2626"
                                    : "#64748b",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {moment(item.createdAt).format("MMM DD, YYYY")}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, item)}
                          sx={{
                            backgroundColor: "#f1f5f9",
                            "&:hover": { backgroundColor: "#e2e8f0" },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={5}
                      sx={{ color: "#051140" }}
                    >
                      No News Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Pagination
          page={page}
          onPageChange={onPageChange}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      </div>

      {/* Row Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
            mt: 1,
            minWidth: 160,
            borderRadius: "10px",
            "& .MuiMenuItem-root": {
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: 500,
              gap: "10px",
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/manage-news/edit/${selectedNews?._id}`, {
              state: { item: selectedNews },
            });
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: "#64748b" }} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        {selectedNews?.status === "Published" && (
          <MenuItem onClick={handleStatusToggle}>
            <ListItemIcon>
              <VisibilityOffIcon fontSize="small" sx={{ color: "#f59e0b" }} />
            </ListItemIcon>
            <ListItemText>Disable</ListItemText>
          </MenuItem>
        )}

        {selectedNews?.status === "Disabled" && (
          <MenuItem onClick={handleStatusToggle}>
            <ListItemIcon>
              <PublishIcon fontSize="small" sx={{ color: "#22c55e" }} />
            </ListItemIcon>
            <ListItemText>Publish</ListItemText>
          </MenuItem>
        )}

        {selectedNews?.status !== "Published" && (
          <MenuItem
            onClick={() => {
              setDeleteId(selectedNews?._id || "");
              setDeleteOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: "#ef4444" }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <WarnModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        handleDelete={handleDelete}
        name="News"
      />
    </MainContainer>
  );
};

export default NewsPage;
